import type { ComponentTypeEmits, EmitFn } from 'vue';
import { omit } from 'lodash-es';
import { useEmitAsProps } from 'reka-ui';

/** `v-model:selection` already relays this one; forwarding it again would emit every change twice. */
const MODEL_RELAYED_HANDLERS = ['onUpdate:selection'];

/**
 * Vue's own `EmitsToProps` collapses to `{}` for an emits **interface** — interfaces have no
 * implicit index signature, so they fail its `extends ObjectEmitsOptions` branch and every key
 * silently disappears. Mapping the keys directly keeps the handlers typed.
 */
type ForwardedHandlers<TEmits> = {
	[K in keyof TEmits & string as `on${Capitalize<K>}`]: TEmits[K] extends unknown[] ? (...args: TEmits[K]) => void : never;
};

/**
 * Turns every emit a listing-layout block declares into an `onEvent` prop, so the block can hand
 * the whole set to its layout component in one `v-bind`.
 *
 * Hand-written `@event="$emit(...)"` lists drop listeners without a warning once the declared
 * emits and the template drift apart: Vue strips handlers for declared emits out of `$attrs`, so
 * an unforwarded event reaches neither the layout nor the parent (MONO-2971).
 */
export function useForwardedLayoutEmits<TEmits extends ComponentTypeEmits>(emit: EmitFn<TEmits>): ForwardedHandlers<TEmits> {
	// `useEmitAsProps` is typed against a bare emit signature and returns `Record<string, any>`: it
	// reads the compiled `emits` array, so its keys are only knowable at runtime — including the
	// `update:selection` that `defineModel` adds and `TEmits` never names.
	const handlers = useEmitAsProps(emit as (name: string, ...args: unknown[]) => void);

	return omit(handlers, MODEL_RELAYED_HANDLERS) as ForwardedHandlers<TEmits>;
}
